import { useEffect, useState } from 'react';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import DownloadIcon from '@mui/icons-material/Download';
import dayjs from 'dayjs';
import {
  AlertSeverity,
  DownloadTask,
  isTerminalStatus,
  setAlert,
  TaskStatus,
  useAppDispatch,
  useGetDownloadTasksQuery,
  useLazyGetDownloadUrlQuery,
} from 'api';

// While any visible task is still working, refresh the list on this cadence.
const POLL_INTERVAL_MS = 5000;

type ChipColor = React.ComponentProps<typeof Chip>['color'];

const STATUS_COLOR: Record<TaskStatus, ChipColor> = {
  Queued: 'default',
  Running: 'info',
  Completed: 'success',
  Failed: 'error',
  Cancelled: 'default',
};

const formatDate = (value: string | null) =>
  value ? dayjs(value).format('MMM D, YYYY h:mm A') : '—';

export const DownloadTasksTable = () => {
  const dispatch = useAppDispatch();
  // Poll only while something is in flight; 0 disables polling.
  const [pollingInterval, setPollingInterval] = useState(0);
  const { data, isLoading, isFetching } = useGetDownloadTasksQuery(undefined, {
    pollingInterval,
  });
  const [getDownloadUrl] = useLazyGetDownloadUrlQuery();

  useEffect(() => {
    const hasActive = data?.some(task => !isTerminalStatus(task.status));
    setPollingInterval(hasActive ? POLL_INTERVAL_MS : 0);
  }, [data]);

  const handleDownload = async (taskId: string) => {
    try {
      const result = await getDownloadUrl(taskId).unwrap();
      if (result?.url) {
        // Navigating to the SAS URL triggers the browser's file download.
        window.location.href = result.url;
      }
    } catch {
      dispatch(
        setAlert({
          message:
            'The download link could not be generated. The file may have expired — try again or re-run the download.',
          severity: AlertSeverity.Error,
        })
      );
    }
  };

  const columns: GridColDef<DownloadTask>[] = [
    {
      field: 'createdAt',
      headerName: 'Requested',
      flex: 1.2,
      minWidth: 180,
      valueFormatter: (value: string) => formatDate(value),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params: GridRenderCellParams<DownloadTask>) => (
        <Chip
          label={params.row.status}
          color={STATUS_COLOR[params.row.status] ?? 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'rowCount',
      headerName: 'Rows',
      width: 120,
      type: 'number',
      valueFormatter: (value: number | null) =>
        value == null ? '—' : value.toLocaleString(),
    },
    {
      field: 'fileName',
      headerName: 'File',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<DownloadTask>) => {
        const { status, fileName, errorMessage } = params.row;
        if (status === 'Failed' && errorMessage) {
          return (
            <Tooltip title={errorMessage}>
              <Typography variant="body2" color="error" noWrap>
                {errorMessage}
              </Typography>
            </Tooltip>
          );
        }
        return (
          <Typography variant="body2" noWrap>
            {fileName ?? '—'}
          </Typography>
        );
      },
    },
    {
      field: 'completedAt',
      headerName: 'Completed',
      flex: 1.2,
      minWidth: 180,
      valueFormatter: (value: string | null) => formatDate(value),
    },
    {
      field: 'actions',
      headerName: 'Download',
      width: 110,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<DownloadTask>) => {
        const ready = params.row.status === 'Completed';
        return (
          <Tooltip
            title={ready ? 'Download CSV' : 'Available once the task completes'}
          >
            <span>
              <IconButton
                color="primary"
                disabled={!ready}
                onClick={() => handleDownload(params.row.taskId)}
                aria-label="Download CSV"
              >
                <DownloadIcon />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <DataGridPro<DownloadTask>
        rows={data ?? []}
        columns={columns}
        getRowId={row => row.taskId}
        loading={isLoading || (isFetching && !data)}
        pagination
        disableRowSelectionOnClick
        initialState={{
          sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] },
          pagination: { paginationModel: { pageSize: 25 } },
        }}
      />
    </Box>
  );
};
